type MidiSong = import('./song.civet').default
type { MidiEvent } from @danielx/lib

// One playable note with its noteOn/noteOff pair resolved and its position
// converted through the tempo map, so practice and game sessions can query
// timing without walking song.events again.
export type IndexedNote =
  c: number
  n: number
  startBeat: number
  endBeat: number
  startTime: number
  endTime: number

// Flatten the song's event list into notes sorted by start time. Pairing
// matches the views' drawing loops: a FIFO per (channel, note), so nested
// same-pitch notes close in the order they opened. Notes with no matching off
// run to the song's end.
export buildNoteIndex := (song: MidiSong): IndexedNote[] ->
  {ticksPerBeat} := song
  notes: IndexedNote[] := []
  open := new Map<number, IndexedNote[]>

  song.events.forEach (e: MidiEvent) ->
    {type} := e
    return unless type is "noteOn" or type is "noteOff"
    {c, n} := e
    beat := e.tick / ticksPerBeat
    k := (c << 7) + n

    if type is "noteOn"
      note: IndexedNote := {
        c, n
        startBeat: beat
        endBeat: NaN
        startTime: song.timeAtBeat(beat)
        endTime: NaN
      }
      notes.push note

      list .= open.get k
      unless list
        list = []
        open.set k, list
      list.push note
    else
      list := open.get k
      return unless list and list.length
      note := list.shift()!
      note.endBeat = beat
      note.endTime = song.timeAtBeat(beat)

  songEndBeat := song.beatLength()
  songEndTime := song.timeAtBeat(songEndBeat)
  open.forEach (list) ->
    list.forEach (note) ->
      note.endBeat = songEndBeat
      note.endTime = songEndTime

  notes.sort (a, b) ->
    a.startTime - b.startTime or a.n - b.n

  notes

// One run of the ":k" duplicate-suffix scheme shared by every generator of
// "tick:channel:pitch" note ids (this file and the Synthesia importer): feed
// each note's key in the chosen traversal order, get its unique id back.
// Caveat the counter can't fix: which physical note of an identical-key
// group gets ":1" depends on the caller's order — only notes that differ
// solely by duration can be told apart by it, so the ambiguity is confined
// to same-tick same-pitch same-channel stacks.
export noteIdCounter := ->
  counts := new Map<string, number>
  (key: string) ->
    k := counts.get(key) ?? 0
    counts.set key, k + 1
    if k then `${key}:${k}` else key

// Ids in the Synthesia importer's format — "tick:channel:pitch", ":k" suffix
// for the k-th duplicate — assigned in index order, parallel to `index`.
// Practice judging (chartNotes) and the setup editor's hand split both use
// this, so a split part's notes are exactly the ones the chart matches.
export indexNoteIds := (index: IndexedNote[], ticksPerBeat: number): string[] ->
  nextId := noteIdCounter()
  index.map (note) ->
    nextId `${Math.round(note.startBeat * ticksPerBeat)}:${note.c}:${note.n}`

export default buildNoteIndex
